import CompanyService, { Entities } from "../../../../services/CompanyService";
import { FilterCollection } from "../../../../services/FilterService";
import { PickByValue } from "../../../../utils/Utils";
import AdvancedFilters, {
  AdvancedFiltersContext
} from "../../../shared/components/AdvancedFilters";
import Dropdown from "../../../shared/components/Dropdown";
import { CheckBoxEditor } from "../../../shared/components/Editors";
import { ImportTemplateListResponse } from "../../../shared/entities/ImportTemplateListResponse";
import { TranslationService } from "../../../../services/TranslationService";
import Alarm from "./Components/Alarm";
import DateFormat from "./Components/DateFormat";
import Dictionaries from "./Components/Dictionaries";
import EditItem from "./Components/EditItem";
import ExcelOptions from "./Components/ExcelOptions";
import PrecedingProcesses from "./Components/PrecedingProcesses";
import SeparatorWithFixedColumns from "./Components/SeparatorWithFixedColumns";
import UploadTemplate from "./Components/UploadTemplate";
import ImportTemplateEditBase, {
  ImportTemplateComponentProps
} from "./ImportTemplateEditBase";
import AdvancedFilterService from "../../../../services/AdvancedFilterService";
import { useContext, useEffect, useState } from "react";
import Collapsable from "../../../shared/bootstrap/Collapsable";
import { InvoiceSetFields } from "../../../../services/InvoiceService";
import ToastContext from "../../../shared/bootstrap/Toast";

const ImportTemplateEditInvoiceUpdate = ({
  data,
  reload,
  importsList = undefined,
  entityId = undefined
}: {
  data?: ImportTemplateListResponse.Item;
  reload: () => void;
  importsList?: ImportTemplateListResponse;
  entityId?: number;
}) => {
  const [showDetail, setShowDetail] = useState(false);

  const contactOptionalByRef = CompanyService.getSetting(
    "allowioupdatebyreferencenumber"
  );

  const requiredIds = contactOptionalByRef
    ? ["referenceNumber"]
    : ["contact", "referenceNumber"];
  const advancedFiltersContext = useContext(AdvancedFiltersContext);
  useEffect(() => {
    advancedFiltersContext.setShow(true);
  }, [advancedFiltersContext]);

  const Header = () => {
    return (
      <div className="m-3">
        <h5 className="mb-4">
          {TranslationService.translate.ImportConfigurationOfNewInvoices}
        </h5>
        <details className="mt-3" onClick={() => setShowDetail((x) => !x)}>
          <summary className="ps-1 mb-2">
            <h6 style={{ display: "unset" }}>
              {TranslationService.translate.SeeTheRequirementsOfTheFileToImport}
            </h6>
          </summary>
        </details>
        <Collapsable show={showDetail}>
          {TranslationService.translate.RequirementsOfTheFileToImport.split(
            "\n"
          ).map((x, i) => (
            <div key={i} className="text-muted ms-3">
              {x}
            </div>
          ))}
        </Collapsable>
      </div>
    );
  };

  return (
    <ImportTemplateEditBase
      entityId={entityId}
      AdvancedOptions={AdvancedOptions}
      Header={Header}
      reload={reload}
      requiredFieldIds={requiredIds}
      data={data}
      showName
    />
  );
};

const AdvancedOptions = ({
  data,
  updateModel,
  model,
  setModel,
  importsList,
  requiredManager
}: ImportTemplateComponentProps) => {
  const { showToast } = useContext(ToastContext);
  const updateNumberModel =
    <
      T extends keyof PickByValue<
        ImportTemplateListResponse.Item,
        number | undefined
      >
    >(
      key: T
    ) =>
    (value: number) => {
      setModel((x) => ({ ...x, [key]: value }));
    };
  const applyFilters = (filters: string[] | undefined) =>
    setModel((x) => ({
      ...x,
      IOFilter: AdvancedFilterService.filterStringsToQueryString(filters)
    }));

  const customPersonIdentityOnChange = updateNumberModel(
    "CustomPersonIdentityID"
  );

  // Sin deps: corre después de cada render, siempre después del useEffect de ValidationMessage
  // Esto garantiza que la sobreescritura persiste aunque ValidationMessage resetee isValid
  useEffect(() => {
    if (!CompanyService.getSetting("allowcustomfieldimport")) return;
    if (!model.CustomPersonIdentityID) return;
    requiredManager.getValidation("referenceNumber").isValid = () => true;
  });

  const getColumnName = (id: number): string => {
    if (id > 0) return "additional-" + id;
    const invoiceFieldMap: Record<number, string> = {
      [InvoiceSetFields.personName]: "contact",
      [InvoiceSetFields.groupId]: "group",
      [InvoiceSetFields.referenceNumber]: "referenceNumber",
      [InvoiceSetFields.amount]: "amount",
      [InvoiceSetFields.description]: "description",
      [InvoiceSetFields.ioStatus]: "iostatus",
      [InvoiceSetFields.currency]: "currency",
      [InvoiceSetFields.emittedDate]: "createdDate",
      [InvoiceSetFields.dueDate]: "dueDate"
    };
    return invoiceFieldMap[id] ?? id.toString();
  };

  const dropdownItems = [
    ...CompanyService.getAdditionalDefinitionsFiltered(Entities.Invoice).map(
      (x) => ({ value: x.AdditionalDefinitionID, text: x.Name })
    )
  ];

  const customValidation = requiredManager.getValidation(
    "validationCustomPersonIdentityID"
  );
  customValidation.isValid = () => {
    if (!model.CustomPersonIdentityID) {
      return true;
    }
    const columnName = getColumnName(model.CustomPersonIdentityID as number);
    const foundColumn = model.Columns?.find((x) => x.name === columnName);
    const valid = foundColumn !== undefined && !!foundColumn.ix;
    if (!valid) {
      showToast(
        TranslationService.translate
          .TheFieldSelectedForUpdateMustHaveASpecifiedColumn
      );
    }
    return valid;
  };

  return (
    <div>
      <small className="text-muted">
        {TranslationService.translate.IfYouLeaveFormatBlank}
      </small>
      <DateFormat
        defaultValue={data?.DateFormat}
        updateModel={updateModel("DateFormat")}
        label={TranslationService.translate.IssueDateFormat}
      />
      <DateFormat
        defaultValue={data?.DueDateFormat}
        updateModel={updateModel("DueDateFormat")}
        label={TranslationService.translate.DueDateFormat}
      />
      <CheckBoxEditor
        message={
          TranslationService.translate
            .UpdateExistingInvoiceDataWhenSynchronizing
        }
        onChange={updateModel("UpdateIOs")}
        defaultValue={data?.UpdateIOs}
      />
      {CompanyService.getIsAdmin() && (
        <div
          className="py-2 mb-3"
          style={{ paddingLeft: 5, marginLeft: -5, backgroundColor: "#f5eebe" }}
        >
          <CheckBoxEditor
            message={"Actualizar datos de facturas pagadas"}
            onChange={updateModel("IncludePaid")}
            defaultValue={data?.IncludePaid}
          />
          <CheckBoxEditor
            message={"Procesar facturas con monto 0 en el archivo"}
            onChange={updateModel("processZeroIOs")}
            defaultValue={data?.processZeroIOs}
          />
        </div>
      )}
      <EditItem label={TranslationService.translate.WhenInvoiceRepeated}>
        <Dropdown
          onChange={updateNumberModel("OnRepeat")}
          defaultValue={data?.OnRepeat}
          items={[
            { value: 0, text: TranslationService.translate.DiscardFile },
            {
              value: 1,
              text: TranslationService.translate.DiscardRepeatedInvoices
            },
            {
              value: 2,
              text: TranslationService.translate.SumRepeatedInvoicesAmount
            },
            { value: 3, text: TranslationService.translate.TakeFirst },
            { value: 4, text: TranslationService.translate.TakeLast }
          ]}
        />
      </EditItem>
      {CompanyService.canEditTemplate() && (
        <UploadTemplate
          formatsAllowed=".xlsx,.csv"
          model={model}
          updateModel={updateModel}
        />
      )}
      <SeparatorWithFixedColumns
        updateModel={updateModel}
        data={data}
        model={model}
      />
      <ExcelOptions data={data} updateModel={updateModel} />
      {CompanyService.getSetting("allowimportdictionary") && (
        <>
          <hr className="my-5" />
          <Dictionaries data={data} model={model} setModel={setModel} />
        </>
      )}
      {CompanyService.getSetting("partialiosync") && (
        <>
          <hr className="my-5" />
          <h5 className="mb-3">
            {TranslationService.translate.FilterInvoices}
          </h5>
          <AdvancedFilters
            page={FilterCollection.Invoice}
            onFilterApply={applyFilters}
            defaultValue={data?.IOFilter}
            allowClosing={false}
          />
        </>
      )}

      {CompanyService.getSetting("allowcustomfieldimport") && (
        <>
          <hr className="my-5" />
          <EditItem
            label={TranslationService.translate.UpdateDataUsingTheField}
          >
            <Dropdown
              onChange={customPersonIdentityOnChange}
              defaultValue={data?.CustomPersonIdentityID}
              items={dropdownItems}
              optionLabel={TranslationService.translate.Select}
            />
          </EditItem>
        </>
      )}
      {CompanyService.getSetting("allowimportpredecessor") && (
        <>
          <hr className="my-5" />
          <PrecedingProcesses
            data={data}
            model={model}
            setModel={setModel}
            importsList={importsList}
          />
        </>
      )}
      <hr className="my-5" />
      <Alarm
        data={data}
        updateModel={updateModel}
        setModel={setModel}
        model={model}
      />
    </div>
  );
};

export default ImportTemplateEditInvoiceUpdate;
